import { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { Icon } from '@/components/kit';

export interface NoteFieldHandle {
  focus: () => void;
}

interface Props {
  value: string;
  onChange: (next: string) => void;
  label: string;
  placeholder: string;
  /** Muted placeholder (a prompt) or plain (the canvas's "—"). */
  mutedPlaceholder?: boolean;
  disabled?: boolean;
  maxLength?: number;
  /** Tab (without Shift) leaves the field here instead of the DOM order. */
  onTab?: () => void;
  /** Hide the pencil — while focused, the caret is the affordance. */
  pencil?: boolean;
}

/** Collapse the caret to the end of the text. */
function caretToEnd(el: HTMLElement) {
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}

/**
 * A NOTE, TYPED IN PLACE — quoted, wrapping, with the pencil right after the
 * closing quote: “rested 5 min before the reading” ✎.
 *
 * The canvas draws a note as running text, so it wraps to a second line inside
 * its card and the pencil follows the last word. An <input> cannot wrap and a
 * <textarea> cannot put a quote and a pencil after its last word, so the text
 * itself is the editable thing (plain text only; Enter never adds a line).
 */
export const NoteField = forwardRef<NoteFieldHandle, Props>(function NoteField(
  { value, onChange, label, placeholder, mutedPlaceholder = true, disabled = false, maxLength = 300, onTab, pencil = true },
  ref,
) {
  const el = useRef<HTMLSpanElement>(null);
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (!el.current || disabled) return;
      el.current.focus();
      caretToEnd(el.current);
    },
  }), [disabled]);

  /* React never renders the text (it would fight the caret); it is written
     here only when it differs — a new patient, a draft restored, a reset. */
  useLayoutEffect(() => {
    if (el.current && el.current.textContent !== value) el.current.textContent = value;
  }, [value]);

  return (
    <>
      {value ? '“' : null}
      <span
        ref={el}
        role="textbox"
        aria-label={label}
        aria-multiline={false}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        contentEditable={disabled ? false : 'plaintext-only'}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        className={mutedPlaceholder ? 'nurse-note nurse-note-muted' : 'nurse-note'}
        onInput={(e) => {
          const node = e.currentTarget;
          let text = (node.textContent ?? '').replace(/\n/g, ' ');
          if (text.length > maxLength) {
            text = text.slice(0, maxLength);
            node.textContent = text;
            caretToEnd(node);
          }
          onChange(text);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault();
          if (e.key === 'Tab' && !e.shiftKey && onTab) {
            e.preventDefault();
            onTab();
          }
        }}
      />
      {value ? '”' : null}
      {pencil ? <> <Icon name="pencil" size="sm" className="mut" /></> : null}
    </>
  );
});
